import { friendlyDbError } from '../utils/friendlyDbError';
import { create } from 'zustand';
import { supabase } from '../supabase';
import type { Document } from '../types';

interface DocumentStore {
  documents: Document[];
  loading: boolean;
  error: string | null;
  fetchDocuments: () => Promise<void>;
  fetchClientDocuments: (clientId: string) => Promise<void>;
  fetchProjectDocuments: (projectId: string) => Promise<void>;
  uploadDocument: (file: File, metadata: { client_id?: string; project_id?: string; deal_id?: string }) => Promise<Document | null>;
  deleteDocument: (id: string) => Promise<boolean>;
}

export const useDocumentStore = create<DocumentStore>((set, get) => ({
  documents: [],
  loading: false,
  error: null,

  fetchDocuments: async () => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('organization_id', profileData.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ documents: data || [], loading: false });
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  fetchClientDocuments: async (clientId) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('client_id', clientId)
        .eq('organization_id', profileData.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ documents: data || [], loading: false });
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  fetchProjectDocuments: async (projectId) => {
    set({ loading: true, error: null });
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      const { data, error } = await supabase
        .from('documents')
        .select('*')
        .eq('project_id', projectId)
        .eq('organization_id', profileData.organization_id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      set({ documents: data || [], loading: false });
    } catch (error) {
      set({ error: friendlyDbError(error), loading: false });
    }
  },

  uploadDocument: async (file, metadata) => {
    try {
      // First upload the file to storage
      const fileName = `${Date.now()}-${file.name}`;
      const filePath = `documents/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get the public URL
      const { data: urlData } = supabase.storage
        .from('documents')
        .getPublicUrl(filePath);

      if (!urlData.publicUrl) {
        throw new Error('Failed to get public URL');
      }

      // Get current user's organization_id
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('User not authenticated');

      const { data: userProfile } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();

      if (!userProfile?.organization_id) throw new Error('User not associated with an organization');

      // Create document record in the database
      const { data: document, error: dbError } = await supabase
        .from('documents')
        .insert({
          name: file.name,
          type: file.type,
          size: file.size,
          url: urlData.publicUrl,
          client_id: metadata.client_id,
          project_id: metadata.project_id,
          deal_id: metadata.deal_id,
          organization_id: userProfile.organization_id,
          created_by: userData.user.id,
        })
        .select()
        .single();

      if (dbError) throw dbError;

      set({ documents: [document, ...get().documents] });
      return document;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return null;
    }
  },

  deleteDocument: async (id) => {
    try {
      // Get current user's organization_id
      const { data: userData, error: userError } = await supabase.auth.getUser();
      
      if (userError) throw userError;
      
      // Get user profile to get organization_id
      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('organization_id')
        .eq('id', userData.user.id)
        .single();
      
      if (profileError) throw profileError;
      
      if (!profileData?.organization_id) {
        throw new Error('Organization ID not found for current user');
      }

      // First get the document to get the file path
      const { data: document, error: getError } = await supabase
        .from('documents')
        .select('url')
        .eq('id', id)
        .eq('organization_id', profileData.organization_id)
        .single();

      if (getError) throw getError;

      // Extract the file path from the URL
      const url = new URL(document.url);
      const filePath = url.pathname.split('/').slice(-2).join('/');

      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('documents')
        .remove([filePath]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('documents')
        .delete()
        .eq('id', id)
        .eq('organization_id', profileData.organization_id);

      if (dbError) throw dbError;

      set({ documents: get().documents.filter(doc => doc.id !== id) });
      return true;
    } catch (error) {
      set({ error: friendlyDbError(error) });
      return false;
    }
  },
}));